import { DialogBox } from '@/components/ui/dialog';
import { base64ToFile } from '@/lib/utils';
import imageCompression from 'browser-image-compression';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import './cropper.css';

interface CropperProps {
  open: boolean;
  onClose: () => void;
  aspectRatio?: number;
  selectedImage?: File;
  setLoading?: (value: boolean) => void;
  setUploadedImage?: (name: File) => void;
  setSelectedImage?: React.Dispatch<React.SetStateAction<File | undefined>>;
  setCroppedImageL?: React.Dispatch<React.SetStateAction<File | undefined>>;
}

const ImageCropper: React.FC<CropperProps> = ({
  aspectRatio = 16 / 9,
  onClose,
  open,
  selectedImage,
  setLoading,
  setSelectedImage,
  setCroppedImageL,
}) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const [src, serSrc] = useState<string>();
  const [croppedImage, setCroppedImage] = useState<File>();
  const onCrop = async () => {
    const cropper = cropperRef.current?.cropper;
    const fileData = cropper?.getCroppedCanvas().toDataURL();
    if (fileData) setCroppedImage(base64ToFile(fileData, selectedImage?.name || 'image'));
  };

  const onSelectFile = useCallback(() => {
    if (selectedImage) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const result = reader.result as string;
        serSrc(result);
      });
      reader.readAsDataURL(selectedImage);
    } else {
      serSrc(undefined);
    }
  }, [selectedImage]);

  useEffect(() => {
    onSelectFile();
  }, [selectedImage]);

  const handleOnCrop = async () => {
    setLoading?.(true);
    const options = {
      maxSizeMB: 1,
      maxWidth: 1024,
      maxHeight: 768,
      useWebWorker: true,
      quality: 80,
    };
    if (croppedImage) {
      onClose();

      const compressedImage = await imageCompression(croppedImage, options);
      setSelectedImage?.(undefined);
      setLoading?.(false);
      setCroppedImageL?.(compressedImage);
      setCroppedImage(undefined);
      serSrc(undefined);
    }
  };

  return (
    <DialogBox
      title='Crop image'
      saveButtonText='Save'
      open={open}
      onClose={onClose}
      onSubmit={handleOnCrop}
    >
      <Cropper
        className='max-h-[350px]'
        src={src}
        initialAspectRatio={aspectRatio}
        guides={false}
        ref={cropperRef}
        viewMode={1}
        crop={onCrop}
        minCropBoxHeight={10}
        minCropBoxWidth={10}
        height={600}
        background={false}
        responsive={true}
        autoCropArea={1}
        aspectRatio={aspectRatio}
        checkOrientation={false}
      />
    </DialogBox>
  );
};

export default ImageCropper;
